/**
 * supabaseBridge — alimente le store Zustand depuis Supabase (lecture + temps réel).
 *
 * Mode "live" : remplace la simulation mock. Charge l'état initial depuis la base,
 * puis s'abonne aux changements realtime → l'UI reflète la vraie base en direct.
 *
 * En Phase 2, le backend agents (Railway) écrit dans ces tables, et le dashboard
 * se met à jour automatiquement ici. Zéro changement d'UI nécessaire.
 */
import { supabase } from './supabaseClient';
import { useSimulationStore } from '@/store/useSimulationStore';
import type { Room } from '@/types/room';
import type { Agent } from '@/types/agent';
import type { Store } from '@/types/store';
import type { Task } from '@/types/task';
import type { ActivityEvent } from '@/types/event';
import type { GlobalMetrics } from '@/types/metrics';

/* eslint-disable @typescript-eslint/no-explicit-any */

const mapRoom = (r: any): Room => ({
  id: r.id, name: r.name, label: r.label, type: r.type, color: r.color,
  pixelX: r.pixel_x, pixelY: r.pixel_y, pixelW: r.pixel_w, pixelH: r.pixel_h,
  waypoints: r.waypoints ?? [], adjacentRoomIds: r.adjacent_room_ids ?? [],
  maxOccupancy: r.max_occupancy, primaryAgentId: r.primary_agent_id,
  storeId: r.store_id, agentsPresent: r.agents_present ?? [],
});

const mapAgent = (a: any): Agent => ({
  id: a.id, codename: a.codename, roleName: a.role_name, role: a.role,
  color: a.color, status: a.status, currentRoomId: a.current_room_id,
  targetRoomId: a.target_room_id, position: a.position ?? { x: 0, y: 0, facing: 'down' },
  stats: a.stats ?? {}, currentTaskId: a.current_task_id, personality: a.personality ?? '',
  online: a.online, tokensBurned: a.tokens_burned ?? 0,
  pixelPath: a.pixel_path ?? [], statusDuration: a.status_duration ?? 0,
});

const mapStore = (s: any): Store => ({
  id: s.id, name: s.name, niche: s.niche, color: s.color, status: s.status,
  metrics: s.metrics ?? {}, assignedAgentIds: s.assigned_agent_ids ?? [],
  roomId: s.room_id, platform: s.platform, emoji: s.emoji ?? '',
  description: s.description ?? '', launchDate: s.launch_date ?? '',
});

const mapTask = (t: any): Task => ({
  id: t.id, title: t.title, description: t.description ?? '', status: t.status,
  priority: t.priority, type: t.type, agentId: t.agent_id, storeId: t.store_id,
  createdAt: Date.parse(t.created_at), updatedAt: Date.parse(t.updated_at),
  completedAt: t.completed_at ? Date.parse(t.completed_at) : null,
  estimatedTokens: t.estimated_tokens ?? 0, actualTokens: t.actual_tokens ?? 0,
  progress: t.progress ?? 0,
});

const mapEvent = (e: any): ActivityEvent => ({
  id: e.id, type: e.type, agentId: e.agent_id, storeId: e.store_id,
  message: e.message, timestamp: Date.parse(e.created_at), read: e.read,
});

const mapMetrics = (m: any): GlobalMetrics => ({
  totalTokensBudget: m.total_tokens_budget, tokensUsedToday: m.tokens_used_today,
  totalRevenue: Number(m.total_revenue), totalMargin: Number(m.total_margin),
  totalOrders: m.total_orders, activeTasks: m.active_tasks,
  pendingValidations: m.pending_validations, agentsOnline: m.agents_online,
  dayNumber: m.day_number, tick: m.tick,
  revenueHistory: m.revenue_history ?? [], tokenHistory: m.token_history ?? [],
});

async function loadAll() {
  if (!supabase) return;
  const store = useSimulationStore.getState();
  const [rooms, agents, stores, tasks, events, metrics] = await Promise.all([
    supabase.from('rooms').select('*'),
    supabase.from('agents').select('*'),
    supabase.from('stores').select('*'),
    supabase.from('tasks').select('*').order('created_at', { ascending: false }),
    supabase.from('events').select('*').order('created_at', { ascending: false }).limit(100),
    supabase.from('metrics').select('*').eq('id', 1).single(),
  ]);

  if (rooms.data) store.setRooms(rooms.data.map(mapRoom));
  if (agents.data) store.setAgents(agents.data.map(mapAgent));
  if (stores.data) store.setStores(stores.data.map(mapStore));
  if (tasks.data) store.setTasks(tasks.data.map(mapTask));
  if (events.data) store.setEvents(events.data.map(mapEvent));
  if (metrics.data) store.setMetrics(mapMetrics(metrics.data));
}

/** Démarre le mode live : chargement initial + abonnements realtime. */
export async function startSupabaseBridge() {
  if (!supabase) {
    console.warn('[supabaseBridge] Supabase non configuré — bascule sur mock');
    return false;
  }
  await loadAll();

  // Realtime : à chaque changement sur une table, on recharge (tables petites).
  supabase
    .channel('mission-control')
    .on('postgres_changes', { event: '*', schema: 'public' }, () => {
      void loadAll();
    })
    .subscribe();

  // Filet de sécurité : polling régulier (si le Realtime n'est pas activé sur les
  // tables, le dashboard reste quand même à jour en quasi-temps réel).
  setInterval(() => { void loadAll(); }, 5000);

  console.log('[supabaseBridge] Mode LIVE actif — données depuis Supabase (realtime + polling 8s)');
  return true;
}
